// LiveHlsPlayer - double-buffered live HLS player for POV switching.
//
// On web we render real, SAME-ORIGIN <video> elements and drive them with
// hls.js (falling back to native HLS on Safari/iOS). An earlier player
// embedded the stream in a `data:` URI iframe, which gives the document an
// opaque/null origin; mobile browsers refuse to delegate autoplay permission
// and block cross-origin media loading from an opaque-origin frame, so the
// stream never started on phones. A same-origin <video> with
// autoplay+muted+playsInline plays on both iOS and Android.
//
// Why TWO elements: a single-element player has to destroy its pipeline on
// every src change, which paints black for the manifest round-trips plus
// liveSyncDurationCount worth of buffering. Here a src change PRELOADS the
// new feed invisibly in the standby element while the outgoing one keeps
// playing, and only swaps once the incoming element has actually painted a
// frame - the same picture-driven pattern the ceremony handoff proved live
// (hold until the element's own playing/loadeddata, never a clock).
//
// The incoming feed is also seeked to the wall-clock moment the outgoing
// feed is showing (utils/feedAlignment, #EXT-X-PROGRAM-DATE-TIME) so every
// POV lands on the same race clock. Alignment is advisory - any failure
// falls back to the default live-edge join and the switch proceeds.
//
// Mute contract: the `muted` prop is CONTROLLED and applied to the active
// element only. Standby elements always preload muted (two feeds must never
// be audible at once) and adopt the real preference at flip. If an unmuted
// play() is rejected by the browser, the player mutes, retries, and reports
// via onAutoplayBlocked - the refusal is surfaced, never persisted as the
// viewer's choice (same principle as useCeremonyAudio).
//
// Poster (optional, 10-15): a `poster` image URL draws as a cover layer over
// the first load until its element actually plays, so the viewer sees the
// streamer's picture instead of a black box. Not the <video> poster
// attribute: a standby element flips in on loadeddata, before it plays, and
// a poster attribute would flash on every switch. With no poster prop there
// is no layer and no listener (EventWatch and TeamStream pass none).
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { WebView } from 'react-native-webview';
import Hls from 'hls.js';

import {
  computeAlignedStartPosition,
  refineAlignmentBeforeFlip,
  sampleFeedClock,
} from '../utils/feedAlignment';

// Byte-identical to the config the single-element player shipped with - the
// buffer tuning trades a few seconds of latency-behind-live for a smoother
// feed. (Note: with lowLatencyMode false, hls.js's rate-based catch-up is
// inert; the >maxLatency hard seek is the only latency enforcement.)
const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: false,            // favor stability over bleeding-edge latency
  liveSyncDurationCount: 4,         // keep ~4 segments of cushion ahead of the playhead
  liveMaxLatencyDurationCount: 12,  // only force catch-up if we fall this far behind
  maxLiveSyncPlaybackRate: 1.5,     // speed up gently to catch live (no jarring seek)
  maxBufferLength: 30,              // seconds of forward buffer to hold when available
  maxMaxBufferLength: 60,
  backBufferLength: 30,             // cap retained played-back buffer (memory)
};

// Short crossfade: after alignment the two pictures show (nearly) the same
// moment, so a long dissolve would only showcase residual misalignment. The
// outgoing element fades out ON TOP of the already-opaque incoming one -
// fade-out-over-opaque never dips brightness the way a symmetric crossfade
// over black does.
const FLIP_FADE_MS = 220;

// A pending switch that hasn't painted in this long is abandoned (stay on
// the outgoing feed). Never force-flip to an unpainted element - that would
// reintroduce the black gap this component exists to kill.
const MAX_PENDING_MS = 12000;

// One quiet retry after an abandoned switch, in case the target feed was
// mid-hiccup. Only while the app still wants the same src.
const RETRY_DELAY_MS = 5000;

// Upper bound on waiting for the 'seeked' event after a refine micro-seek.
const SEEKED_PAINT_TIMEOUT_MS = 400;

const prefersReducedMotion = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch (e) {
    return false;
  }
};

const LiveHlsPlayer = ({
  src,
  muted = true,
  videoRef,
  onActiveVideoChange,
  onAutoplayBlocked,
  onSwitchFailed,
  poster,
}) => {
  const slotsRef = useRef(null);
  if (!slotsRef.current) {
    slotsRef.current = {
      A: { video: null, hls: null, src: null, metaCleanup: null },
      B: { video: null, hls: null, src: null, metaCleanup: null },
    };
  }
  const slots = slotsRef.current;

  const activeKeyRef = useRef(null);
  // Dev-only console handle so alignment can be measured during verification
  // (e.g. __liveHls.slots[__liveHls.activeKey()].hls.playingDate). Stripped
  // from production builds. Registered only for the caller that passes
  // videoRef (EventWatch's single spectator player) - the handle is
  // single-instance by contract, and TeamStream mounts up to 8 concurrent
  // players that must not fight over it.
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production' && videoRef) {
    window.__liveHls = { slots, activeKey: () => activeKeyRef.current };
  }
  const pendingRef = useRef(null); // { key, src, cancelGate, maxTimer }
  const fadeRef = useRef(null);    // { key, timer }
  const retryTimerRef = useRef(null);
  const retriedSrcRef = useRef(null);
  const srcRef = useRef(src);
  const mutedRef = useRef(muted);

  const [visibleKey, setVisibleKey] = useState(null);
  const [fadingKey, setFadingKey] = useState(null);
  // Poster layer: done once the shown element plays; a URL that failed to
  // load is hidden instead of drawing a broken image.
  const [posterDone, setPosterDone] = useState(false);
  const [failedPoster, setFailedPoster] = useState(null);

  // Callbacks live in refs so the src effect doesn't churn on parent renders.
  const onActiveVideoChangeRef = useRef(onActiveVideoChange);
  const onAutoplayBlockedRef = useRef(onAutoplayBlocked);
  const onSwitchFailedRef = useRef(onSwitchFailed);
  onActiveVideoChangeRef.current = onActiveVideoChange;
  onAutoplayBlockedRef.current = onAutoplayBlocked;
  onSwitchFailedRef.current = onSwitchFailed;

  const setVideoA = useCallback((el) => { slots.A.video = el; }, [slots]);
  const setVideoB = useCallback((el) => { slots.B.video = el; }, [slots]);

  function tryPlay(video) {
    const p = video.play?.();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        if (video.muted) return; // muted play refused: terminal no-op
        video.muted = true;
        video.play?.().catch(() => {});
        // Truthful icon, not a preference change - the parent flips its
        // muted state, which our [muted] effect re-applies (already true on
        // the element, and it never calls play(), so no loop).
        onAutoplayBlockedRef.current?.();
      });
    }
  }

  function teardownSlot(key) {
    const slot = slots[key];
    if (slot.metaCleanup) { slot.metaCleanup(); slot.metaCleanup = null; }
    if (slot.hls) {
      try { slot.hls.destroy(); } catch (e) { /* already dead */ }
      slot.hls = null;
    }
    const video = slot.video;
    if (video) {
      try {
        video.pause?.();
        // Covers both pipelines: hls.js attaches via a blob src, Safari
        // native via the manifest URL.
        if (video.getAttribute && video.getAttribute('src') != null) {
          video.removeAttribute('src');
          video.load?.();
        }
      } catch (e) { /* teardown is best-effort */ }
    }
    slot.src = null;
  }

  function finalizeFade() {
    const fade = fadeRef.current;
    if (!fade) return;
    fadeRef.current = null;
    clearTimeout(fade.timer);
    teardownSlot(fade.key);
    setFadingKey(null);
  }

  function cancelPending() {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    clearTimeout(pending.maxTimer);
    pending.cancelGate?.();
    teardownSlot(pending.key);
  }

  function clearRetry() {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }

  function loadIntoSlot(key, url) {
    const slot = slots[key];
    const video = slot.video;
    if (!video) return false;
    teardownSlot(key);
    slot.src = url;
    video.muted = true; // preload rule; the real preference lands at activation

    if (Hls.isSupported()) {
      const hls = new Hls(HLS_CONFIG);
      slot.hls = hls;

      // Coarse alignment: once the incoming playlist (with backfilled PDT)
      // is in, retarget loading to the outgoing feed's wall-clock moment.
      // First load has no outgoing feed and keeps the default join.
      hls.once(Hls.Events.LEVEL_LOADED, (_evt, data) => {
        try {
          if (slot.hls !== hls) return; // slot was retargeted meanwhile
          const activeKey = activeKeyRef.current;
          const outgoing = activeKey && activeKey !== key ? slots[activeKey] : null;
          if (!outgoing) return;
          const target = sampleFeedClock(outgoing.hls, outgoing.video);
          const pos = computeAlignedStartPosition(target, data && data.details);
          if (process.env.NODE_ENV !== 'production') {
            const d = data && data.details;
            console.log('[LiveHls] align', JSON.stringify({
              target: target ? target.toISOString() : null,
              pos,
              edge: d && d.edge,
              first: d && d.fragments && d.fragments.length
                ? { start: d.fragments[0].start, gap: !!d.fragments[0].gap }
                : null,
              nFrags: d && d.fragments ? d.fragments.length : 0,
              targetduration: d && d.targetduration,
              hasPDT: d && d.hasProgramDateTime,
              live: d && d.live,
            }));
          }
          if (pos != null) hls.startLoad(pos);
        } catch (e) { /* alignment is advisory */ }
      });

      // A fatal error on a PENDING feed abandons the switch and stays on the
      // outgoing picture. The active feed keeps parity with the old player:
      // no recovery handling (the element's own 'error' event still reaches
      // the parent's feed listeners).
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data || !data.fatal) return;
        if (pendingRef.current?.key === key && slot.hls === hls) {
          abandonPendingSwitch(url);
        }
      });

      hls.loadSource(url);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari / iOS - native HLS, no MSE needed
      video.src = url;
    } else {
      slot.src = null;
      return false;
    }

    const onMeta = () => tryPlay(video);
    video.addEventListener('loadedmetadata', onMeta);
    slot.metaCleanup = () => video.removeEventListener('loadedmetadata', onMeta);
    return true;
  }

  // Resolves once the element has a decoded frame ready: playing/loadeddata,
  // the ceremony-handoff precedent (readyState >= 2 means the frame paints
  // the moment the element is revealed). Deliberately no
  // requestVideoFrameCallback layer: rVFC may never fire for an opacity-0
  // element or a hidden tab, and its safety-valve timer opened a race where
  // a stale gate could force-commit a NEWER pending switch before it painted.
  function armFlipGate(video, commit) {
    let done = false;
    const cleanup = () => {
      video.removeEventListener('playing', onReady);
      video.removeEventListener('loadeddata', onReady);
    };
    const onReady = () => {
      if (done) return;
      done = true;
      cleanup();
      commit();
    };
    video.addEventListener('playing', onReady);
    video.addEventListener('loadeddata', onReady);
    return () => { done = true; cleanup(); };
  }

  // Gate fired: optionally hop the incoming feed forward to close the drift
  // that accumulated while it buffered, then commit on the post-seek frame.
  // Every async hop re-checks that THIS pending (by object identity, not
  // slot key - a rapid re-switch reuses the key) is still the live one.
  function scheduleCommit(pending) {
    if (pendingRef.current !== pending) return;
    const slot = slots[pending.key];
    const video = slot.video;
    const finish = () => commitFlip(pending);
    const activeKey = activeKeyRef.current;
    const outgoing = activeKey && activeKey !== pending.key ? slots[activeKey] : null;
    if (process.env.NODE_ENV !== 'production' && outgoing) {
      const a = sampleFeedClock(outgoing.hls, outgoing.video);
      const b = sampleFeedClock(slot.hls, video);
      console.log('[LiveHls] preflip delta ms:', a && b ? a.getTime() - b.getTime() : null);
    }
    const seeked = outgoing
      ? refineAlignmentBeforeFlip(slot.hls, video, () =>
          sampleFeedClock(outgoing.hls, outgoing.video))
      : false;
    if (process.env.NODE_ENV !== 'production') {
      console.log('[LiveHls] refine seeked:', seeked);
    }
    if (!seeked) { finish(); return; }
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      video.removeEventListener('seeked', onSeeked);
      finish(); // commitFlip re-checks pending identity
    };
    const onSeeked = () => go();
    video.addEventListener('seeked', onSeeked);
    setTimeout(go, SEEKED_PAINT_TIMEOUT_MS);
  }

  function commitFlip(pending) {
    if (pendingRef.current !== pending) return; // cancelled/replaced meanwhile
    const key = pending.key;
    pendingRef.current = null;
    clearTimeout(pending.maxTimer);
    pending.cancelGate?.();

    const fromKey = activeKeyRef.current;
    const slot = slots[key];
    const video = slot.video;

    video.muted = mutedRef.current; // adopt the viewer's real choice NOW
    if (video.paused) tryPlay(video); // belt: some browsers pause on unmute

    activeKeyRef.current = key;
    retriedSrcRef.current = null;
    if (videoRef) videoRef.current = video; // overlay controls retarget instantly
    onActiveVideoChangeRef.current?.(video); // parent re-binds its feed listeners

    finalizeFade(); // a rapid re-switch mid-fade must not leak the old slot
    setVisibleKey(key);
    if (fromKey && fromKey !== key) {
      if (prefersReducedMotion()) {
        teardownSlot(fromKey);
      } else {
        // Mute the outgoing element for its fade: the incoming one adopted
        // the real preference above, and two feeds must never be audible at
        // once - an unmuted outgoing would double-talk for the fade span.
        const fadingVideo = slots[fromKey].video;
        if (fadingVideo) fadingVideo.muted = true;
        setFadingKey(fromKey);
        fadeRef.current = {
          key: fromKey,
          timer: setTimeout(() => {
            fadeRef.current = null;
            teardownSlot(fromKey);
            setFadingKey(null);
          }, FLIP_FADE_MS + 30),
        };
      }
    }
  }

  function abandonPendingSwitch(failedSrc) {
    cancelPending();
    if (retriedSrcRef.current !== failedSrc) {
      // First failure: retry quietly in case the feed was mid-hiccup. The
      // parent is only told once the retry has also failed, so it can
      // reconcile its selection against the picture actually showing.
      retriedSrcRef.current = failedSrc;
      clearRetry();
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (
          srcRef.current === failedSrc &&
          !pendingRef.current &&
          activeKeyRef.current &&
          slots[activeKeyRef.current].src !== failedSrc
        ) {
          beginSwitch(failedSrc);
        }
      }, RETRY_DELAY_MS);
    } else {
      const activeKey = activeKeyRef.current;
      const activeSrc = activeKey ? slots[activeKey].src : null;
      onSwitchFailedRef.current?.(failedSrc, activeSrc);
    }
  }

  function beginSwitch(url) {
    finalizeFade();   // at most two roles may exist: active + standby
    cancelPending();  // rapid re-switch retargets the standby slot
    const standbyKey = activeKeyRef.current === 'A' ? 'B' : 'A';
    if (!loadIntoSlot(standbyKey, url)) return;
    const video = slots[standbyKey].video;
    const pending = {
      key: standbyKey,
      src: url,
      cancelGate: null,
      maxTimer: setTimeout(() => abandonPendingSwitch(url), MAX_PENDING_MS),
    };
    // The gate closes over the pending OBJECT: every commit path re-checks
    // pendingRef.current === pending, so a gate surviving a rapid re-switch
    // (same slot key, new pending) can never commit the wrong feed.
    pending.cancelGate = armFlipGate(video, () => scheduleCommit(pending));
    pendingRef.current = pending;
  }

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    srcRef.current = src;
    clearRetry();

    if (!src) {
      cancelPending();
      finalizeFade();
      if (activeKeyRef.current) {
        teardownSlot(activeKeyRef.current);
        activeKeyRef.current = null;
      }
      if (videoRef) videoRef.current = null;
      onActiveVideoChangeRef.current?.(null);
      setVisibleKey(null);
      return;
    }

    const activeKey = activeKeyRef.current;
    if (!activeKey) {
      // First load: straight into the active slot, exactly like the old
      // single-element player. The ceremony's handoff hold masks the wait.
      if (loadIntoSlot('A', src)) {
        const video = slots.A.video;
        video.muted = mutedRef.current; // init true -> first autoplay is muted
        activeKeyRef.current = 'A';
        if (videoRef) videoRef.current = video;
        onActiveVideoChangeRef.current?.(video);
        setVisibleKey('A');
      }
      return;
    }

    if (slots[activeKey].src === src) {
      // Same feed re-asserted (e.g. a poll refresh); drop any pending switch
      // that now points somewhere the app no longer wants.
      cancelPending();
      return;
    }

    beginSwitch(src);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    mutedRef.current = muted;
    if (Platform.OS !== 'web') return;
    const key = activeKeyRef.current;
    const video = key ? slots[key].video : null;
    if (video) video.muted = muted;
    // Deliberately no play() here: unmuting while paused must not resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  // Poster: hide the cover once the shown element plays. Keyed on
  // visibleKey, so a flip before the first frame waits on the incoming
  // element instead. A src that drops to null starts over (new first load).
  // Without a poster this effect does nothing. Layout effect: a poster that
  // arrives while the video already plays is marked done before paint, so it
  // never flashes over live video.
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !poster) return undefined;
    if (!visibleKey) { setPosterDone(false); return undefined; }
    if (posterDone) return undefined;
    const video = slots[visibleKey].video;
    if (!video) return undefined;
    if (!video.paused && video.readyState >= 3) { setPosterDone(true); return undefined; }
    const onPlaying = () => setPosterDone(true);
    video.addEventListener('playing', onPlaying);
    return () => video.removeEventListener('playing', onPlaying);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poster, visibleKey, posterDone]);

  // Unmount: ternary flip to an embed provider, feed loss, or navigation.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    return () => {
      clearRetry();
      cancelPending();
      finalizeFade();
      teardownSlot('A');
      teardownSlot('B');
      activeKeyRef.current = null; // a remount must take the first-load path
      if (videoRef) videoRef.current = null;
      onActiveVideoChangeRef.current?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (Platform.OS !== 'web') {
    // Native app: hand the URL to the platform WebView (best-effort).
    return (
      <WebView
        source={{ uri: src }}
        style={styles.host}
        allowsInlineMediaPlayback={true}
        mediaPlaybackRequiresUserAction={false}
      />
    );
  }

  // Raw react-dom <video> elements (not RNW Views), so plain CSS transitions
  // apply and React stays the owner of the inline style. The container View
  // supplies the black letterbox; the fading element sits on top (zIndex 2)
  // over the fully-opaque incoming one (zIndex 1).
  const videoStyle = (key) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    // The fading element renders at opacity 0 WITH a transition: the browser
    // animates from its previous computed value (1), giving the fade-out.
    opacity: fadingKey === key ? 0 : (visibleKey === key ? 1 : 0),
    zIndex: fadingKey === key ? 2 : (visibleKey === key ? 1 : 0),
    transition: fadingKey === key ? `opacity ${FLIP_FADE_MS}ms ease-out` : 'none',
    pointerEvents: 'none',
  });

  return (
    <View style={styles.host}>
      <video ref={setVideoA} autoPlay muted playsInline style={videoStyle('A')} />
      <video ref={setVideoB} autoPlay muted playsInline style={videoStyle('B')} />
      {poster && poster !== failedPoster ? (
        <img
          src={poster}
          alt=""
          draggable={false}
          onError={() => setFailedPoster(poster)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Above both video slots inside this host; the parent's controls
            // overlay still paints over the whole host.
            zIndex: 3,
            opacity: posterDone ? 0 : 1,
            transition: posterDone && !prefersReducedMotion() ? `opacity ${FLIP_FADE_MS}ms ease-out` : 'none',
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  host: {
    flex: 1,
    backgroundColor: '#000',
    overflow: 'hidden',
  },
});

export default LiveHlsPlayer;
